import type { AdvertiserCompanyDomainRelationDTO } from '../api/types';

export interface GoogleFilterAvailableOptions {
  companyIds: number[];
  domains: string[];
  advertiserIds: string[];
}

/**
 * Google filter relations:
 * company_id ↔ domain ↔ advertiser_id
 * */
export class GoogleAdvertiserBiMap {
  private readonly allData: AdvertiserCompanyDomainRelationDTO[];
  private cachedAll: GoogleFilterAvailableOptions | null = null;
  private cache = new Map<string, GoogleFilterAvailableOptions>();

  constructor(data: AdvertiserCompanyDomainRelationDTO[]) {
    this.allData = data;
  }

  private extractUnique(items: AdvertiserCompanyDomainRelationDTO[]): GoogleFilterAvailableOptions {
    return {
      companyIds: [...new Set(items.map((item) => item.company_id))],
      domains: [...new Set(items.map((item) => item.domain))],
      advertiserIds: [...new Set(items.map((item) => item.advertiser_id))],
    };
  }

  getAll(): GoogleFilterAvailableOptions {
    if (!this.cachedAll) {
      this.cachedAll = this.extractUnique(this.allData);
    }
    return this.cachedAll;
  }

  /**
   * Filters relation rows by any combination of selected company, domain, and advertiser.
   * Each filter dropdown passes the other two dimensions (Facebook-style bidirectional cascade).
   */
  getForSelection(
    companyIds: number[] = [],
    domains: string[] = [],
    advertiserIds: string[] = []
  ): GoogleFilterAvailableOptions {
    const hasCompanies = companyIds.length > 0;
    const hasDomains = domains.length > 0;
    const hasAdvertisers = advertiserIds.length > 0;

    if (!hasCompanies && !hasDomains && !hasAdvertisers) return this.getAll();

    const cacheKey = `sel:${[...companyIds].sort().join(',')};${[...domains].sort().join(',')};${[...advertiserIds].sort().join(',')}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    let items = this.allData;

    if (hasCompanies) {
      const companySet = new Set(companyIds);
      items = items.filter((item) => companySet.has(item.company_id));
    }

    if (hasDomains) {
      const domainSet = new Set(domains);
      items = items.filter((item) => domainSet.has(item.domain));
    }

    if (hasAdvertisers) {
      const advertiserSet = new Set(advertiserIds);
      items = items.filter((item) => advertiserSet.has(item.advertiser_id));
    }

    const result = this.extractUnique(items);
    this.cache.set(cacheKey, result);
    return result;
  }
}
